import cn from 'classnames'
import { useMemo, useState } from 'react'

import { useHomePageBogoHeroImages } from '@mc/bogo'
import { useBogo } from '@mc/bogo-utils'
import {
  Button,
  HoverHandler,
  IconPauseDefault,
  IconPlayDefault,
} from '@mc/design-system'
import Experiment from '@mc/experiments-react'
import { useTranslation } from '@mc/i18n'
import { useHomePagePromoHeroImages } from '@mc/promos'
import { usePromotion } from '@mc/promo-utils'

import {
  CAROUSEL_SPRINGBOARD_EXP_FLAG,
  convertCarouselImagesToHeroImagePairs,
  DoubleColumnSpringboardCarousel,
  HorizontalSpringboardCarousel,
  SingleColumnSpringboardCarousel,
  useCarouselSpringboardExp,
} from 'experiments/pen-4889_v1_hp_carousel_springboard'

import {
  DoubleColumnCarouselItem,
  SingleColumnCarouselItem,
} from '../CarouselItems'
import { type HeroImage, heroImages, IMAGE_RESOLUTIONS } from './constants'
import { actions, trackClickCarouselHero, trackPlayPauseClick } from './events'

import styles from './Carousel.module.scss'

type CarouselLayoutProps = {
  mobileLayout: React.ReactNode
  tabletLayout: React.ReactNode
  desktopLayout: React.ReactNode
}

const CarouselLayout = ({
  mobileLayout,
  tabletLayout,
  desktopLayout,
}: CarouselLayoutProps) => (
  <div className='mc-h-100'>
    <div className='d-block d-md-none'>{mobileLayout}</div>
    <div className='d-none d-md-block d-lg-none'>{tabletLayout}</div>
    <div className='d-none d-lg-block'>{desktopLayout}</div>
  </div>
)

export const Carousel = () => {
  const { t } = useTranslation('@mc/next')
  const [isAnimationActive, setIsAnimationActive] = useState(true)

  // pen-4889_v1 additional hover handler state to not override user's button animation choice with hovering:
  const [isPlayPauseButtonClicked, setIsPlayPauseButtonClicked] =
    useState(false)

  const { inPen4889Experiment, isPen4889Variant } = useCarouselSpringboardExp()

  const toggleCarouselAnimation = () => {
    trackPlayPauseClick(
      isAnimationActive
        ? actions.PAUSE_CAROUSEL_ANIMATION
        : actions.RESUME_CAROUSEL_ANIMATION,
    )
    setIsAnimationActive(!isAnimationActive)
    setIsPlayPauseButtonClicked(!isPlayPauseButtonClicked)
  }

  const handleHoverChange = (hovering?: boolean) => {
    if (!isPlayPauseButtonClicked) {
      if (hovering === true) setIsAnimationActive(false)
      if (hovering === false) setIsAnimationActive(true)
    }
  }

  return (
    <div
      className={`mc-overflow--hidden mc-h-100 ${styles.carouselContainer} ${isPen4889Variant ? styles.pen4889Variant1 : ''}`}
    >
      {/* We are adding the onClick because we want to fire an event
        but we don't want to show it as a button to the user */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        id='hero-carousel'
        className={cn('d-flex', 'flex-column', styles.imageLoopContent, {
          [styles.pen4889Experiment]: inPen4889Experiment,
          [styles.pen4889Variant1]: isPen4889Variant,
        })}
        style={{
          animationPlayState: isAnimationActive ? 'running' : 'paused',
        }}
        onClick={() => {
          if (!inPen4889Experiment)
            trackClickCarouselHero(actions.CLICK_CAROUSEL_HERO)
        }}
      >
        <Experiment
          name={CAROUSEL_SPRINGBOARD_EXP_FLAG}
          default={() => (
            <CarouselLayout
              desktopLayout={<DoubleColumnCarousel size='lg' />}
              mobileLayout={<DoubleColumnCarousel size='sm' />}
              tabletLayout={<SingleColumnCarousel size='md' />}
            />
          )}
          variant_1={() => (
            <HoverHandler
              onChange={({ hovering }) => handleHoverChange(hovering)}
            >
              {({ props }) => (
                <CarouselLayout
                  {...props}
                  desktopLayout={<DoubleColumnSpringboardCarousel />}
                  mobileLayout={<HorizontalSpringboardCarousel />}
                  tabletLayout={<SingleColumnSpringboardCarousel />}
                />
              )}
            </HoverHandler>
          )}
        />
      </div>
      <Button
        className={`position-absolute mc-corners--circle mc-p-2 ${styles.playPauseButton}`}
        kind='tertiary'
        onClick={toggleCarouselAnimation}
        size='small'
        aria-label={
          isAnimationActive
            ? t('home.hero.a11y.pauseAriaLabel')
            : t('home.hero.a11y.playAriaLabel')
        }
      >
        {isAnimationActive && (
          <IconPauseDefault className={styles.playPauseIcon} />
        )}
        {!isAnimationActive && (
          <IconPlayDefault className={styles.playPauseIcon} />
        )}
      </Button>
    </div>
  )
}

type ColumnCarouselProps = {
  size: 'sm' | 'md' | 'lg'
}

const useCarouselImages = () => {
  const { isBogoOfferEligible } = useBogo()
  const { isPromotionOn } = usePromotion()
  const { inPen4889Experiment } = useCarouselSpringboardExp()

  const updatedHeroImages = useMemo(
    () => convertCarouselImagesToHeroImagePairs(),
    [],
  )

  const baseHeroImages = inPen4889Experiment ? updatedHeroImages : heroImages

  const bogoHeroImages = useHomePageBogoHeroImages(baseHeroImages)
  const promoImages = useHomePagePromoHeroImages(baseHeroImages)

  const source: HeroImage[] = useMemo(() => {
    if (isBogoOfferEligible) return bogoHeroImages
    if (isPromotionOn) return promoImages
    return baseHeroImages
  }, [
    isBogoOfferEligible,
    bogoHeroImages,
    isPromotionOn,
    promoImages,
    baseHeroImages,
  ])

  const doubleImages = useMemo(() => source.concat(source), [source])

  const singleImages = useMemo(() => {
    // Extract all column images into a single array
    const allColumnImages = source.flatMap(({ firstColumn, secondColumn }) => [
      firstColumn,
      secondColumn,
    ])

    return allColumnImages.concat(allColumnImages)
  }, [source])

  return { doubleImages, singleImages }
}

export const DoubleColumnCarousel = ({ size }: ColumnCarouselProps) => {
  const imageWidth = IMAGE_RESOLUTIONS[size].width
  const imageHeight = IMAGE_RESOLUTIONS[size].height
  const { doubleImages: images } = useCarouselImages()

  return images.map(({ firstColumn, secondColumn }, index) => (
    <DoubleColumnCarouselItem
      imageWidth={imageWidth}
      imageHeight={imageHeight}
      key={firstColumn + secondColumn + index}
      firstColumn={firstColumn}
      secondColumn={secondColumn}
    />
  ))
}

export const SingleColumnCarousel = ({ size }: ColumnCarouselProps) => {
  const imageWidth = IMAGE_RESOLUTIONS[size].width
  const imageHeight = IMAGE_RESOLUTIONS[size].height
  const { singleImages: images } = useCarouselImages()

  return images.map((item: string, index) => (
    <SingleColumnCarouselItem
      imageWidth={imageWidth * 2}
      imageHeight={imageHeight * 2}
      key={item + index}
      item={item}
    />
  ))
}
